import { useEffect, useRef, useState } from 'react'

const hasFiles = (event: DragEvent): boolean => Array.from(event.dataTransfer?.types ?? []).includes('Files')

/** Window-wide drag and drop of a single file. */
export const useFileDrop = (onFile: (file: File) => void, enabled = true): boolean => {
    const [dragging, setDragging] = useState(false)
    const depth = useRef(0)
    const callback = useRef(onFile)

    useEffect(() => {
        callback.current = onFile
    }, [onFile])

    useEffect(() => {
        if (!enabled) return

        const onEnter = (event: DragEvent) => {
            if (!hasFiles(event)) return

            event.preventDefault()
            depth.current++
            setDragging(true)
        }

        const onOver = (event: DragEvent) => {
            if (!hasFiles(event)) return

            event.preventDefault()

            if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
        }

        const onLeave = (event: DragEvent) => {
            if (!hasFiles(event)) return

            depth.current = Math.max(0, depth.current - 1)

            if (depth.current === 0) setDragging(false)
        }

        const onDrop = (event: DragEvent) => {
            if (!hasFiles(event)) return

            event.preventDefault()
            depth.current = 0
            setDragging(false)

            const file = event.dataTransfer?.files?.[0]

            if (file) callback.current(file)
        }

        window.addEventListener('dragenter', onEnter)
        window.addEventListener('dragover', onOver)
        window.addEventListener('dragleave', onLeave)
        window.addEventListener('drop', onDrop)

        return () => {
            window.removeEventListener('dragenter', onEnter)
            window.removeEventListener('dragover', onOver)
            window.removeEventListener('dragleave', onLeave)
            window.removeEventListener('drop', onDrop)
        }
    }, [enabled])

    return dragging
}
